import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { playAlarm, toggleFullscreen } from "@/lib/tv";
import { CornersOut, WarningDiamond, ShieldCheck, SpeakerHigh, SpeakerSlash } from "@phosphor-icons/react";

const SEV = {
  critical: { bg: "bg-[#FF3B30]", text: "text-[#FF3B30]", label: "CRITICAL" },
  high: { bg: "bg-[#FF9F0A]", text: "text-[#FF9F0A]", label: "HIGH" },
  medium: { bg: "bg-[#0A84FF]", text: "text-[#0A84FF]", label: "MEDIUM" },
  low: { bg: "bg-[#71717A]", text: "text-[#A1A1AA]", label: "LOW" },
};

export default function AlertSpotlight() {
  const [spot, setSpot] = useState(null);
  const [ticker, setTicker] = useState([]);
  const [sound, setSound] = useState(true);
  const rootRef = useRef(null);
  const lastIdRef = useRef(null);
  const soundRef = useRef(true);
  soundRef.current = sound;

  useEffect(() => {
    const tick = async () => {
      try {
        const { data } = await api.get("/wall/spotlight");
        setSpot(data);
        const a = data.alert;
        if (a && a.id !== lastIdRef.current) {
          lastIdRef.current = a.id;
          if ((a.severity === "critical" || a.severity === "high") && soundRef.current) playAlarm(a.severity);
        }
      } catch (e) { /* keep last state */ }
      api.get("/alerts").then((r) => setTicker(r.data.slice(0, 40))).catch(() => {});
    };
    tick();
    const t = setInterval(tick, 4000);
    return () => clearInterval(t);
  }, []);

  const alert = spot?.alert;
  const sev = SEV[alert?.severity] || SEV.high;

  return (
    <div ref={rootRef} className="relative bg-[#0A0A0C] min-h-[calc(100vh-8rem)] flex flex-col" data-testid="alert-spotlight">
      <span className="absolute bottom-1 right-2 z-10 text-[9px] font-mono-data tracking-[0.2em] text-[#3f3f46] uppercase pointer-events-none">Powered by A V Tech</span>
      <div className="flex items-center gap-3 mb-3">
        <span className="font-display font-black uppercase tracking-tighter text-sm flex items-center gap-2">
          <WarningDiamond size={18} weight="bold" className="text-[#FF3B30]" /> Alert Spotlight TV
        </span>
        <span className="font-mono-data text-[10px] text-[#71717A]">Auto-jumps to the camera that just triggered · driven by the Surveillance Wall</span>
        <button data-testid="spotlight-sound" onClick={() => setSound((s) => !s)}
          className="ml-auto flex items-center gap-1.5 px-3 py-2 bg-[#1A1A1E] border border-white/10 text-xs hover:border-white/30 transition-colors">
          {sound ? <SpeakerHigh size={15} className="text-[#32D74B]" /> : <SpeakerSlash size={15} className="text-[#71717A]" />}
          Alarm {sound ? "On" : "Off"}
        </button>
        <button data-testid="spotlight-fullscreen" onClick={() => toggleFullscreen(rootRef.current)}
          className="flex items-center gap-1.5 px-3 py-2 bg-[#1A1A1E] border border-white/10 text-xs hover:border-white/30 transition-colors">
          <CornersOut size={15} /> Fullscreen
        </button>
      </div>

      <div className="flex-1 flex flex-col">
        {alert ? (
          <div className={`relative flex-1 min-h-[50vh] bg-black border-2 ${sev.bg.replace("bg-", "border-")} overflow-hidden`} data-testid="spotlight-view">
            {spot.frame_b64 ? (
              <img src={`data:image/jpeg;base64,${spot.frame_b64}`} alt="alert" className="w-full h-full object-cover opacity-90" />
            ) : spot.frame_url ? (
              <img src={spot.frame_url} alt="alert" className="w-full h-full object-cover opacity-80" />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-[#3f3f46]"><WarningDiamond size={60} weight="thin" /></div>
            )}
            <div className={`absolute top-4 left-4 flex items-center gap-2 ${sev.bg} px-3 py-1.5 animate-pulse`}>
              <WarningDiamond size={18} weight="fill" className="text-white" />
              <span className="font-display font-black uppercase tracking-wide text-white">{sev.label} · {alert.type}</span>
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black via-black/80 to-transparent">
              <p className="font-display font-black text-2xl uppercase tracking-tighter" data-testid="spotlight-camera">{spot.camera?.name || alert.camera}</p>
              <p className="text-[#E4E4E7] mt-1 max-w-3xl" data-testid="spotlight-message">{alert.message}</p>
              <p className="font-mono-data text-xs text-[#71717A] mt-2">{spot.camera?.zone} · {new Date(alert.ts).toLocaleString()}</p>
            </div>
          </div>
        ) : (
          <div className="flex-1 min-h-[50vh] border border-white/10 flex flex-col items-center justify-center gap-3 text-center" data-testid="spotlight-idle">
            <ShieldCheck size={56} weight="thin" className="text-[#32D74B]" />
            <p className="font-display font-black uppercase tracking-tighter text-xl">All Clear</p>
            <p className="font-mono-data text-xs text-[#71717A] max-w-md">Waiting for triggers. Keep the Surveillance Wall running on your other TV — the moment it flags something, that camera lands here.</p>
          </div>
        )}
      </div>

      {/* Ticker */}
      <div className="mt-3 bg-[#121214] border border-white/10 overflow-hidden" data-testid="spotlight-ticker">
        <div className="flex items-center">
          <span className="shrink-0 bg-[#FF3B30] text-white font-display font-black uppercase text-xs px-3 py-2 tracking-wide">Recent</span>
          <div className="flex-1 overflow-hidden whitespace-nowrap py-2">
            {ticker.length === 0 ? (
              <span className="px-4 font-mono-data text-xs text-[#71717A]">No alerts recorded yet.</span>
            ) : (
              <div className="inline-block animate-[marquee_30s_linear_infinite] whitespace-nowrap">
                {ticker.map((a) => (
                  <span key={a.id} className="mx-6 font-mono-data text-xs">
                    <span className={SEV[a.severity]?.text || "text-[#FF9F0A]"}>▲ {a.type}</span>
                    <span className="text-[#A1A1AA]"> · {a.camera} · {new Date(a.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
